import React, { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import Navbar from './Navbar';
import '../styles/Debts.css';
import { SERVER_URL } from './../config';
import { AuthContext } from './../AuthContext'; // Import AuthContext

const Debts = () => {
  const { token, isAuthenticated } = useContext(AuthContext); // get token and isAuthenticated from context
  const [debts, setDebts] = useState([]);
  const [filteredDebts, setFilteredDebts] = useState([]);
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [editableDebt, setEditableDebt] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');
  const [newDebt, setNewDebt] = useState({
    description: '',
    amount: '',
    due_date: '',
    balance: '',
    active: true,
  });

  // States for filtering
  const [showActive, setShowActive] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  const fetchDebts = useCallback(async () => {
    if (!token || !isAuthenticated) return;
    try {
      const response = await axios.get(`${SERVER_URL}/debts/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
      });
      if (Array.isArray(response.data)) {
        setDebts(response.data);
      } else {
        console.error('Unexpected response format:', response.data);
      }
    } catch (error) {
      console.error('Error fetching debts:', error);
    }
  }, [token, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchDebts();
    }
  }, [isAuthenticated, fetchDebts]);

  const applyFilters = useCallback(() => {
    const filtered = debts.filter((debt) => {
      const activeMatch = (showActive && debt.active) || (showInactive && !debt.active);
      return activeMatch;
    });
    setFilteredDebts(filtered);
  }, [debts, showActive, showInactive]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleFilterChange = (e) => {
    const { name, checked } = e.target;
    switch (name) {
      case 'showActive':
        setShowActive(checked);
        break;
      case 'showInactive':
        setShowInactive(checked);
        break;
      default:
        break;
    }
  };

  const openModal = (debt) => {
    setSelectedDebt(debt);
    setEditableDebt({ ...debt });
    setUpdateMessage('');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedDebt(null);
    setEditableDebt(null);
    setUpdateMessage('');
    setIsModalOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    let newValue = type === 'checkbox' ? e.target.checked : value;
    setEditableDebt((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleUpdate = async () => {
    if (!selectedDebt) return;
    try {
      const response = await axios.patch(
        `${SERVER_URL}/debts/${selectedDebt.id}/`,
        editableDebt,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        }
      );
      setDebts((prevDebts) =>
        prevDebts.map((debt) => (debt.id === selectedDebt.id ? response.data : debt))
      );
      setUpdateMessage('Debt updated successfully.');
    } catch (error) {
      console.error('Error updating debt:', error);
      setUpdateMessage('Failed to update debt. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!selectedDebt) return;
    try {
      await axios.delete(`${SERVER_URL}/debts/${selectedDebt.id}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      setDebts((prevDebts) => prevDebts.filter((debt) => debt.id !== selectedDebt.id));
      closeModal();
    } catch (error) {
      console.error('Error deleting debt:', error);
    }
  };

  const handleCreateDebt = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${SERVER_URL}/debts/`, newDebt, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      setDebts([...debts, response.data]);
      setShowCreateModal(false);
      setNewDebt({
        description: '',
        amount: '',
        due_date: '',
        balance: '',
        active: true,
      });
    } catch (error) {
      console.error('Error creating debt:', error);
    }
  };

  const handleNewInputChange = (e) => {
    const { name, value, type } = e.target;
    let newValue = type === 'checkbox' ? e.target.checked : value;
    setNewDebt((prevState) => ({
      ...prevState,
      [name]: newValue,
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const formatter = new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
    return formatter.format(date);
  };

  // Calculate total amount
  const totalAmount = filteredDebts.reduce((sum, debt) => sum + parseFloat(debt.amount || 0), 0);

  return (
    <div className="debts">
      <Navbar />
      <div className="content">
        <h1>Debts</h1>
        <button className="create-debt-button" onClick={() => setShowCreateModal(true)}>
          Add Debt
        </button>
        <div className="filter-options">
          <label>
            <input
              type="checkbox"
              name="showActive"
              checked={showActive}
              onChange={handleFilterChange}
            />
            Show Active
          </label>
          <label>
            <input
              type="checkbox"
              name="showInactive"
              checked={showInactive}
              onChange={handleFilterChange}
            />
            Show Inactive
          </label>
        </div>
        <div className="debts-container">
          <table className="debts-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Amount</th>
                <th>Due Date</th>
                <th>Balance</th>
                <th>Active</th>
              </tr>
            </thead>
            <tbody>
              {filteredDebts.map((debt, index) => (
                <tr
                  key={debt.id}
                  className={index % 2 === 0 ? 'even' : 'odd'}
                  onClick={() => openModal(debt)}
                >
                  <td>{debt.description}</td>
                  <td>{debt.amount}</td>
                  <td>{debt.due_date ? formatDate(debt.due_date) : 'N/A'}</td>
                  <td>{debt.balance}</td>
                  <td>{debt.active ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td><strong>Total</strong></td>
                <td><strong>${totalAmount.toFixed(2)}</strong></td>
                <td colSpan="3"></td>
              </tr>
            </tfoot>
          </table>
          <div className="debts-tiles">
            {filteredDebts.map((debt) => (
              <div
                key={debt.id}
                className="debt-tile"
                onClick={() => openModal(debt)}
              >
                <div className="debt-description">{debt.description}</div>
                <div className="debt-amount">
                  <label>Amount: </label>
                  {debt.amount}
                </div>
                <div className="debt-due-date">
                  <label>Due Date: </label>
                  {debt.due_date ? formatDate(debt.due_date) : 'N/A'}
                </div>
                <div className="debt-balance">
                  <label>Balance: </label>
                  {debt.balance}
                </div>
                <div className="debt-active">
                  <label>Active: </label>
                  {debt.active ? 'Yes' : 'No'}
                </div>
              </div>
            ))}
          </div>
        </div>
        {isModalOpen && editableDebt && (
          <div className="modal">
            <div className="modal-content">
              <button
                type="button"
                className="modal-x"
                onClick={closeModal}
                aria-label="Close"
                title="Close"
              >
              </button>
              <h2>Edit Debt</h2>
              <form>
                <label>
                  Description:
                  <input
                    type="text"
                    name="description"
                    value={editableDebt.description}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Amount:
                  <input
                    type="number"
                    name="amount"
                    value={editableDebt.amount}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Due Date:
                  <input
                    type="date"
                    name="due_date"
                    value={editableDebt.due_date || ''}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Balance:
                  <input
                    type="number"
                    name="balance"
                    value={editableDebt.balance}
                    onChange={handleInputChange}
                  />
                </label>
                <label className="inline-checkbox">
                  Active:
                  <input
                    type="checkbox"
                    name="active"
                    checked={editableDebt.active}
                    onChange={handleInputChange}
                  />
                </label>
              </form>
              {updateMessage && (
                <div
                  className={`modal-message ${
                    updateMessage.includes('success') ? 'success' : 'error'
                  }`}
                >
                  {updateMessage}
                </div>
              )}
              <div className="modal-buttons">
                <button className="modal-button" onClick={handleUpdate}>
                  Update
                </button>
                <button className="modal-button delete-button" onClick={handleDelete}>
                  Delete
                </button>
                <button className="modal-button close-button" onClick={closeModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        {showCreateModal && (
          <div className="modal">
            <div className="modal-content">
              <button
                type="button"
                className="modal-x"
                onClick={() => setShowCreateModal(false)}
                aria-label="Close"
                title="Close"
              >
              </button>
              <h2>Create Debt</h2>
              <form onSubmit={handleCreateDebt}>
                <label>
                  Description:
                  <input
                    type="text"
                    name="description"
                    value={newDebt.description}
                    onChange={handleNewInputChange}
                    required
                  />
                </label>
                <label>
                  Amount:
                  <input
                    type="number"
                    name="amount"
                    value={newDebt.amount}
                    onChange={handleNewInputChange}
                    required
                  />
                </label>
                <label>
                  Due Date:
                  <input
                    type="date"
                    name="due_date"
                    value={newDebt.due_date}
                    onChange={handleNewInputChange}
                  />
                </label>
                <label>
                  Balance:
                  <input
                    type="number"
                    name="balance"
                    value={newDebt.balance}
                    onChange={handleNewInputChange}
                    required
                  />
                </label>
                <label className="inline-checkbox">
                  Active:
                  <input
                    type="checkbox"
                    name="active"
                    checked={newDebt.active}
                    onChange={handleNewInputChange}
                  />
                </label>
                <button type="submit">Create</button>
              </form>
              <button
                className="modal-button close-button"
                onClick={() => setShowCreateModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Debts;
